'use client';

import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { CachedContentData } from '@/hooks/useCachedContentData';

// Parallax: the backdrop moves at (1 - factor) of the scroll speed — 0.06 → ~94%, very subtle.
// Kept low on purpose: since the overscan (and thus how far the image is shifted up out of frame at
// rest) is coupled to the factor below, a small factor also keeps the image's top edge nearly in view.
const PARALLAX_FACTOR = 0.06;
// Headroom the parallax layer overscans the hero (top & bottom), and the clamp on the translate.
// Kept equal to the factor on purpose: the translate (factor · |rect.top|) only hits the clamp when
// |rect.top| = height — i.e. exactly when the hero has fully scrolled past the top edge. So the
// parallax runs the hero's entire on-screen travel and never snaps back to page speed while visible.
const PARALLAX_OVERSCAN = PARALLAX_FACTOR;

type SliderImagesProps = {
    showSelectedMovie?: boolean;
    selectedContent1?: CachedContentData;
    selectedContent2?: CachedContentData;
    /** Opt in to the slow Ken-Burns zoom on the active backdrop. Off by default. */
    useKenBurstZoom?: boolean;
};

function SliderImages(props: SliderImagesProps): React.JSX.Element {
    const { showSelectedMovie, selectedContent1, selectedContent2, useKenBurstZoom = false } = props;

    // Two crossfading backdrops; the visible one (active) optionally runs the slow Ken-Burns zoom.
    return (
        <>
            <ZoomingBackdrop
                content={selectedContent1}
                active={!!showSelectedMovie}
                useKenBurstZoom={useKenBurstZoom}
            />
            <ZoomingBackdrop
                content={selectedContent2}
                active={!showSelectedMovie}
                useKenBurstZoom={useKenBurstZoom}
            />
        </>
    );
}

function ZoomingBackdrop({
    content,
    active,
    useKenBurstZoom
}: {
    content?: CachedContentData;
    active: boolean;
    useKenBurstZoom: boolean;
}): React.JSX.Element | null {
    const backdropImageURL = content?.backdropImageURL_ORIGINAL || content?.backdropImage;
    const backdropBlurData = content?.backdropBlurDataImage || content?.backdropImage;

    // Slow Ken-Burns zoom on the visible backdrop: start at rest, then ease up to a small fixed scale
    // on the next frame (one-shot transition, capped — not an infinite animation). The state write
    // happens inside rAF (never synchronously in the effect body), so it runs after paint without a
    // cascading render. Resets when the backdrop is hidden, ready for the next time it becomes active.
    const [zoomedIn, setZoomedIn] = useState(false);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setZoomedIn(active));
        return () => cancelAnimationFrame(frame);
    }, [active]);

    // Scroll parallax on a dedicated layer (NOT the zooming image — its 14s transition would smear the
    // translate). Driven by the hero's viewport position, rAF-throttled, clamped to the overscan, and
    // disabled under prefers-reduced-motion.
    const wrapperRef = useRef<HTMLDivElement>(null);
    const parallaxRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let frame = 0;
        const update = () => {
            frame = 0;
            const wrapper = wrapperRef.current;
            const layer = parallaxRef.current;
            if (!wrapper || !layer) {
                return;
            }
            const rect = wrapper.getBoundingClientRect();
            const max = rect.height * PARALLAX_OVERSCAN;
            // rect.top is 0 at the top of the page and goes negative as the hero scrolls up, so the
            // backdrop drifts downward (lags) as you scroll down.
            const offset = Math.max(-max, Math.min(max, -rect.top * PARALLAX_FACTOR));
            layer.style.transform = `translate3d(0, ${offset}px, 0)`;
        };
        const onScroll = () => {
            if (!frame) {
                frame = requestAnimationFrame(update);
            }
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            if (frame) {
                cancelAnimationFrame(frame);
            }
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    if (!backdropImageURL) {
        return null;
    }

    return (
        // Wrapper owns the opacity crossfade (500ms) and clips both the parallax shift and the zoom.
        <div
            ref={wrapperRef}
            className={cn(
                'absolute inset-0 overflow-hidden',
                'transition-opacity duration-500',
                active ? 'opacity-100' : 'opacity-0'
            )}>
            {/* Parallax layer: overscans the hero top/bottom (derived from PARALLAX_OVERSCAN so it always
                matches the clamp) so the scroll-driven translate never reveals a gap. */}
            <div
                ref={parallaxRef}
                className="absolute inset-x-0 will-change-transform"
                style={{
                    top: `${-PARALLAX_OVERSCAN * 100}%`,
                    height: `${100 + PARALLAX_OVERSCAN * 200}%`
                }}>
                <Image
                    className={cn(
                        'w-full h-full object-cover',
                        content ? 'object-top' : 'object-center',
                        // Force a GPU layer so the very slow sub-pixel scale is interpolated by the
                        // compositor instead of re-rasterised (and pixel-snapped) each frame.
                        'transform-gpu [backface-visibility:hidden] will-change-transform',
                        'transition-transform ease-out',
                        'motion-reduce:transition-none motion-reduce:scale-100',
                        useKenBurstZoom && active && zoomedIn
                            ? 'duration-[14000ms] scale-[1.08]'
                            : 'duration-[2000ms] scale-100'
                    )}
                    src={backdropImageURL}
                    blurDataURL={backdropBlurData}
                    width={2000}
                    height={1000}
                    priority
                    alt=""
                />
            </div>
        </div>
    );
}

export default SliderImages;
